import type { MediaKind } from 'mediasoup-client/lib/RtpParameters';
import type { PeerGroup, PeerResponse, ProducerData, Role } from '../types/common';
import { TrackLabel } from '../types/common';
import { ProducerLabel } from '../proto/generated/producer/producer_label';
import type { Producer as ChannelMessageProducer } from '../proto/generated/producer/producer';
import type { Peer as ChannelMessagePeer } from '../proto/generated/peer/peer';
import { PeerGroup as ChannelMessagePeerGroup } from '../proto/generated/peer/peer_group';
import { PeerRole as ChannelMessagePeerRole } from '../proto/generated/peer/peer_role';
import { ProducerKind as ChannelMessageProducerKind } from '../proto/generated/producer/producer_kind';

const producerKindToMediaKindMapping: Partial<Record<ChannelMessageProducerKind, MediaKind>> = {
  [ChannelMessageProducerKind.PRODUCER_KIND_VIDEO]: 'video',
  [ChannelMessageProducerKind.PRODUCER_KIND_AUDIO]: 'audio',
};

const channelMessagePeerRoleToPeerRole: Partial<Record<ChannelMessagePeerRole, Role>> = {
  [ChannelMessagePeerRole.PEER_ROLE_HOST]: 'host',
  [ChannelMessagePeerRole.PEER_ROLE_AUDIENCE]: 'audience',
};

const producerLabelToTrackLabelMapping: Partial<Record<ProducerLabel, TrackLabel>> = {
  [ProducerLabel.PRODUCER_LABEL_CAMERA]: TrackLabel.Camera,
  [ProducerLabel.PRODUCER_LABEL_MICROPHONE]: TrackLabel.Microphone,
  [ProducerLabel.PRODUCER_LABEL_SCREEN_VIDEO]: TrackLabel.ScreenVideo,
  [ProducerLabel.PRODUCER_LABEL_SCREEN_AUDIO]: TrackLabel.ScreenAudio,
  [ProducerLabel.PRODUCER_LABEL_CUSTOM_VIDEO]: TrackLabel.CustomVideo,
  [ProducerLabel.PRODUCER_LABEL_CUSTOM_AUDIO]: TrackLabel.CustomAudio,
  [ProducerLabel.PRODUCER_LABEL_UNKNOWN]: TrackLabel.Unknown,
  [ProducerLabel.PRODUCER_LABEL_UNSPECIFIED]: TrackLabel.Unknown,
};

const channelMessagePeerGroupToPeerGroupMapping: Partial<Record<ChannelMessagePeerGroup, PeerGroup>> = {
  [ChannelMessagePeerGroup.PEER_GROUP_MODERATOR]: 'moderator',
  [ChannelMessagePeerGroup.PEER_GROUP_USER]: 'user',
};

export const convertChannelMessageProducerKindToMediaKind =
  (producerKind: ChannelMessageProducerKind): MediaKind => {
    const mediaKind = producerKindToMediaKindMapping[producerKind];

    if (!mediaKind) {
      throw new Error(`Unknown producer kind: ${ producerKind }`);
    }

    return mediaKind;
  };

export const convertChannelMessagePeerGroupsToPeerGroups = (
  peerGroup: ChannelMessagePeerGroup,
): PeerGroup => {
  const group = channelMessagePeerGroupToPeerGroupMapping[peerGroup];

  if (!group) {
    throw new Error(`Unknown peer group: ${ peerGroup }`);
  }

  return group;
};

export const convertChannelMessageProducerLabelsToTrackLabels = (
  producerLabel: ProducerLabel,
): TrackLabel => {
  const label = producerLabelToTrackLabelMapping[producerLabel];

  if (!label) {
    throw new Error(`Unknown producer label: ${ producerLabel }`);
  }

  return label;
};

export const convertChannelMessagePeerRoleToRole = (peerRole: ChannelMessagePeerRole): Role => {
  const role = channelMessagePeerRoleToPeerRole[peerRole];

  if (!role) {
    throw new Error(`Unknown peer role: ${ peerRole }`);
  }

  return role;
};

export const convertChannelMessagePeerToPeerResponse = (
  peer: ChannelMessagePeer,
): PeerResponse => {
  return {
    id: peer.id,
    channelIds: peer.channelIds,
    groups: peer.groups.map(convertChannelMessagePeerGroupsToPeerGroups),
    producePermissions: peer.producePermissions.map(convertChannelMessageProducerLabelsToTrackLabels),
    appId: peer.appId,
    producers: peer.producers.map(convertChannelMessageProducerToProducerData),
    uid: peer.uid,
    appData: peer.appData,
    role: convertChannelMessagePeerRoleToRole(peer.role),
    loginDate: peer.loginDate as unknown as Date,
  };
};

export const convertChannelMessageProducerToProducerData = (
  producer: ChannelMessageProducer,
): ProducerData => {
  return {
    id: producer.id,
    kind: convertChannelMessageProducerKindToMediaKind(producer.kind),
    label: convertChannelMessageProducerLabelsToTrackLabels(producer.label),
    peerId: producer.peerId,
    paused: producer.paused,
    encodings: producer.encodings as unknown as ProducerData['encodings'],
    maxSpatialLayer: producer.maxSpatialLayer ?? 0,
  };
};

export const convertPeerGroupsToChannelMessagePeerGroups = (
  peerGroup: PeerGroup,
): ChannelMessagePeerGroup => {
  switch (peerGroup) {
    case 'moderator':
      return ChannelMessagePeerGroup.PEER_GROUP_MODERATOR;
    case 'user':
      return ChannelMessagePeerGroup.PEER_GROUP_USER;
    default:
      return ChannelMessagePeerGroup.PEER_GROUP_UNSPECIFIED;
  }
};
